import { FlatList, Platform, useWindowDimensions } from "react-native";
import { format, formatISO } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import type { AlarmSchedule } from "@visioni/a3s-alarm-schedules";
import { AlarmException } from "@src/services/api/types";
import { View } from "@src/components";
import { useUnistyles } from "react-native-unistyles";
import { memo, useMemo, useCallback, useState, useRef, useImperativeHandle, forwardRef } from "react";
import { buildWeeks, getExceptionColors } from "@src/features/schedules/utils/schedule-utils";
import { sizes } from "@src/constants/sizes";
import { DayColumn } from "./DayColumn";
import { Legend } from "./Legend";
import { WeekNavigationBar } from "./WeekNavigationBar";

interface WeeklyScheduleProps {
  schedule?: AlarmSchedule;
  exceptions?: AlarmException[];
  timezone?: string;
}

export interface WeeklyScheduleRef {
  scrollToPreviousWeek: () => void;
  scrollToNextWeek: () => void;
}

const WeeklyScheduleComponent = forwardRef<WeeklyScheduleRef, WeeklyScheduleProps>(function WeeklySchedule(
  { schedule, exceptions = [], timezone = Intl.DateTimeFormat().resolvedOptions().timeZone },
  ref,
) {
  const today = useMemo(() => toZonedTime(new Date(), timezone), [timezone]);
  const { theme } = useUnistyles();
  const { width: windowWidth } = useWindowDimensions();
  const flatListRef = useRef<FlatList>(null);
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0); // Start at current week (index 0)
  const [currentPeriodIndex, setCurrentPeriodIndex] = useState(0); // Start at current period (index 0) - week on mobile, fortnight on web
  const [containerWidth, setContainerWidth] = useState(0);

  // Use measured container width so web side panels don't break week pagination.
  const width = useMemo(() => {
    if (containerWidth > 0) {
      return containerWidth;
    }
    if (Platform.OS === "web") {
      const maxContentWidth = Math.min(windowWidth, sizes.maxWidthWeb);
      return maxContentWidth;
    }

    return windowWidth;
  }, [containerWidth, windowWidth]);

  // Build periods: fortnights (14 days) on web, weeks (7 days) on mobile
  const periods = useMemo(() => {
    const allWeeks = buildWeeks(today, 0, 8);
    if (Platform.OS !== "web") return allWeeks;

    // Group consecutive weeks into fortnights
    return allWeeks.reduce<Date[][]>((fortnights, week, i, arr) => {
      if (i % 2 === 0) {
        fortnights.push(arr[i + 1] ? [...week, ...arr[i + 1]] : week);
      }
      return fortnights;
    }, []);
  }, [today]);

  // Calculate the week range label for the currently displayed week (e.g. "Feb 9 - Feb 15, 2026")
  const currentWeekLabel = useMemo(() => {
    const currentWeek = periods[currentPeriodIndex];
    if (!currentWeek || currentWeek.length === 0) return "";

    const firstDay = currentWeek[0];
    const lastDay = currentWeek[currentWeek.length - 1];

    const year = format(lastDay, "yyyy");

    // Week spans two months - show "Mon D - Mon D, YYYY"
    return `${format(firstDay, "MMM d")} - ${format(lastDay, "MMM d")}, ${year}`;
  }, [periods, currentPeriodIndex]);

  const scrollToPeriod = useCallback(
    (delta: number) => {
      const newIndex = currentPeriodIndex + delta;
      if (newIndex >= 0 && newIndex < periods.length) {
        flatListRef.current?.scrollToIndex({ index: newIndex, animated: true });
        setCurrentPeriodIndex(newIndex);
      }
    },
    [currentPeriodIndex, periods.length],
  );

  useImperativeHandle(
    ref,
    () => ({
      scrollToPreviousWeek: () => scrollToPeriod(-1),
      scrollToNextWeek: () => scrollToPeriod(1),
    }),
    [scrollToPeriod],
  );

  const canNavigate = { prev: currentPeriodIndex > 0, next: currentPeriodIndex < periods.length - 1 };

  const exceptionColors = useMemo(() => {
    const colorMap: Record<string, string> = {};
    const exceptionColorPalette = getExceptionColors();
    const orangeColor = theme.colors.warning[500] as string; // Orange color for override
    const textColor = theme.colors.text as string; // Text color for silence

    exceptions.forEach((ex, index) => {
      colorMap[ex.id] = exceptionColorPalette[index % exceptionColorPalette.length];
    });

    // Also check schedule rules for silence and override
    if (schedule) {
      schedule.rules.forEach((rule) => {
        if (rule.kind === "absolute_range") {
          const ruleId = rule.id;
          // Check if this is a silence or override rule
          if (ruleId.startsWith("silence:")) {
            colorMap[ruleId] = textColor;
          } else if (ruleId.startsWith("override:")) {
            colorMap[ruleId] = orangeColor;
          }
        }
      });
    }

    return colorMap;
  }, [exceptions, schedule, theme.colors.warning, theme.colors.text]);

  const handleGoToToday = useCallback(() => {
    flatListRef.current?.scrollToIndex({ index: 0, animated: true });
    setCurrentWeekIndex(0);
    setCurrentPeriodIndex(0);
  }, []);

  const isCurrentWeek = useMemo(() => {
    return currentWeekIndex === 0;
  }, [currentWeekIndex]);

  const renderWeek = useCallback(
    ({ item: weekDates }: { item: Date[] }) => {
      return (
        <View
          style={{
            width,
            flexDirection: "row",
            backgroundColor: theme.colors.surface as string,
          }}
        >
          {weekDates.map((d) => (
            <DayColumn
              key={formatISO(d, { representation: "date" })}
              date={d}
              exceptionColors={exceptionColors}
              isToday={format(d, "yyyy-MM-dd") === format(today, "yyyy-MM-dd")}
              schedule={schedule}
              exceptions={exceptions}
              timezone={timezone}
            />
          ))}
        </View>
      );
    },
    [exceptionColors, schedule, exceptions, theme.colors.surface, width, timezone, today],
  );

  return (
    <View flex={1} bg="surface">
      <WeekNavigationBar
        label={currentWeekLabel}
        canGoPrevious={canNavigate.prev}
        canGoNext={canNavigate.next}
        onPrevious={() => scrollToPeriod(-1)}
        onNext={() => scrollToPeriod(1)}
        isCurrentWeek={isCurrentWeek}
        onGoToToday={handleGoToToday}
      />
      <View
        style={{ width: "100%" }}
        onLayout={(event) => {
          const nextWidth = event.nativeEvent.layout.width;
          if (nextWidth > 0 && Math.round(nextWidth) !== Math.round(containerWidth)) {
            setContainerWidth(nextWidth);
          }
        }}
      >
        <FlatList
          ref={flatListRef}
          data={periods}
          keyExtractor={(_, i) => String(i)}
          renderItem={renderWeek}
          horizontal
          showsHorizontalScrollIndicator={false}
          snapToInterval={width}
          decelerationRate="fast"
          bounces={false}
          getItemLayout={(_, index) => ({
            length: width,
            offset: width * index,
            index,
          })}
          onMomentumScrollEnd={(event) => {
            const index = Math.round(event.nativeEvent.contentOffset.x / width);
            setCurrentWeekIndex(index);
            setCurrentPeriodIndex(index);
          }}
          initialScrollIndex={0} // start on current week
          removeClippedSubviews={false}
          maxToRenderPerBatch={3}
          windowSize={3}
          initialNumToRender={3}
        />
      </View>
      <View>
        <Legend exceptions={exceptions} schedule={schedule} />
      </View>
    </View>
  );
});

export const WeeklySchedule = memo(WeeklyScheduleComponent);
